import { getGatewayBaseUrl } from "../config";
import {
  PosReceivableSettlementGatewayClient,
  type ReceivableSettlementResult,
} from "../gateway/receivable-settlement-client";
import type { CashShiftSnapshot, EmployeeRuntimeActor } from "../gateway/identity-client";
import { clearSettledCustomerMemberContext } from "./member-context-lifecycle";
import { applyCanonicalCustomerCreditSummaryReadback } from "./receivable-credit-readback";
import { posRuntimeDb, type JournalRecord, type OutboxRecord } from "./pos-db";
import type { TrustedTerminalIdentity } from "./terminal-state";

export type LocalReceivableSettlementSnapshot = {
  schema: "LOCAL_RECEIVABLE_SETTLEMENT_V1";
  localSettlementId: string;
  rootRequestKey: string;
  organizationId: string;
  collectionBranchId: string;
  nodeId: string;
  performingEmployeeId: string;
  shiftId: string;
  receivableId: string;
  customerId: string;
  amount: number;
  currencyCode: string;
  paymentMethodCode: string;
  settlementMode: string;
  receivingDestinationId: string | null;
  receivingDestinationName: string | null;
  verificationSource: "CASH_TENDERED" | "STAFF_CONFIRMED";
  tenderedAmount: number | null;
  localChangeAmount: number | null;
  localCommittedAt: string;
  canonical: ReceivableSettlementResult | null;
};

export type CommitLocalReceivableSettlementInput = {
  identity: TrustedTerminalIdentity;
  actor: EmployeeRuntimeActor;
  shift: CashShiftSnapshot;
  receivableId: string;
  customerId: string;
  remainingAmount: number;
  amount: number;
  currencyCode: string;
  paymentMethodCode: string;
  settlementMode: string;
  receivingDestinationId: string | null;
  receivingDestinationName: string | null;
  tenderedAmount: number | null;
  localChangeAmount: number | null;
};

type ReceivableSettlementOutboxPayload = {
  receivableId: string;
  amount: number;
  paymentMethodCode: string;
  receivingDestinationId: string | null;
  tenderedAmount: number | null;
  localChangeAmount: number | null;
  verificationSource: "CASH_TENDERED" | "STAFF_CONFIRMED";
  localCommittedAt: string;
};

function randomId() {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function settlementScope(identity: TrustedTerminalIdentity) {
  return `org:${identity.organization_id}:branch:${identity.branch_id}:node:${identity.node_id}`;
}

function settlementLocalId(id: string) {
  return `receivable-settlement:${id}`;
}

function snapshotFromJournal(record: JournalRecord) {
  const snapshot = record.snapshot as LocalReceivableSettlementSnapshot;
  return snapshot?.schema === "LOCAL_RECEIVABLE_SETTLEMENT_V1" ? snapshot : null;
}

async function pendingIntentForReceivable(identity: TrustedTerminalIdentity, receivableId: string) {
  const scopeKey = settlementScope(identity);
  const rows = await posRuntimeDb.journal
    .where("scopeKey")
    .equals(scopeKey)
    .filter((record) => record.kind === "RECEIVABLE_SETTLEMENT" && !["CONFIRMED", "REJECTED"].includes(record.state))
    .toArray();
  return rows.find((record) => snapshotFromJournal(record)?.receivableId === receivableId) ?? null;
}

export async function commitLocalReceivableSettlement(input: CommitLocalReceivableSettlementInput) {
  if (!input.actor.permission_codes.includes("commerce.sale.commit")) throw new Error("COMMERCE_SALE_COMMIT_PERMISSION_REQUIRED");
  if (input.shift.shift_status !== "OPEN") throw new Error("ACTIVE_POS_SHIFT_REQUIRED");
  if (!Number.isFinite(input.amount) || input.amount <= 0) throw new Error("RECEIVABLE_PAYMENT_AMOUNT_INVALID");
  if (!Number.isFinite(input.remainingAmount) || input.remainingAmount <= 0) throw new Error("RECEIVABLE_ALREADY_PAID");
  if (input.amount > input.remainingAmount + 0.005) throw new Error("RECEIVABLE_OVERPAYMENT_NOT_ALLOWED");
  if (!input.paymentMethodCode.trim()) throw new Error("PAYMENT_METHOD_CODE_REQUIRED");

  const existing = await pendingIntentForReceivable(input.identity, input.receivableId);
  if (existing) {
    const snapshot = snapshotFromJournal(existing);
    if (!snapshot) throw new Error("RECEIVABLE_SETTLEMENT_PENDING_INTENT_INVALID");
    return snapshot;
  }

  const settlementMode = input.settlementMode.trim().toUpperCase();
  const isCash = settlementMode === "IMMEDIATE_CASH";
  if (!isCash && settlementMode !== "RECEIVING_DESTINATION") throw new Error("PAYMENT_SETTLEMENT_MODE_NOT_SUPPORTED_V1");
  if (isCash) {
    if (input.receivingDestinationId) throw new Error("CASH_RECEIVING_DESTINATION_NOT_ALLOWED");
    if (input.tenderedAmount == null || input.tenderedAmount < input.amount) throw new Error("TENDERED_AMOUNT_INVALID");
    const expectedChange = input.tenderedAmount - input.amount;
    if (input.localChangeAmount == null || Math.abs(input.localChangeAmount - expectedChange) > 0.005) {
      throw new Error("LOCAL_CHANGE_AMOUNT_MISMATCH");
    }
  } else if (!input.receivingDestinationId) {
    throw new Error("PAYMENT_RECEIVING_DESTINATION_REQUIRED");
  }

  const localSettlementId = randomId();
  const rootRequestKey = `pos-receivable-settlement-v1:${randomId()}`;
  const localCommittedAt = new Date().toISOString();
  const verificationSource = isCash ? "CASH_TENDERED" : "STAFF_CONFIRMED";
  const snapshot: LocalReceivableSettlementSnapshot = {
    schema: "LOCAL_RECEIVABLE_SETTLEMENT_V1",
    localSettlementId,
    rootRequestKey,
    organizationId: input.identity.organization_id,
    collectionBranchId: input.identity.branch_id,
    nodeId: input.identity.node_id,
    performingEmployeeId: input.actor.employee_id,
    shiftId: input.shift.shift_id,
    receivableId: input.receivableId,
    customerId: input.customerId,
    amount: input.amount,
    currencyCode: input.currencyCode,
    paymentMethodCode: input.paymentMethodCode.trim().toUpperCase(),
    settlementMode,
    receivingDestinationId: isCash ? null : input.receivingDestinationId,
    receivingDestinationName: isCash ? null : input.receivingDestinationName,
    verificationSource,
    tenderedAmount: isCash ? input.tenderedAmount : null,
    localChangeAmount: isCash ? input.localChangeAmount : null,
    localCommittedAt,
    canonical: null,
  };
  const payload: ReceivableSettlementOutboxPayload = {
    receivableId: snapshot.receivableId,
    amount: snapshot.amount,
    paymentMethodCode: snapshot.paymentMethodCode,
    receivingDestinationId: snapshot.receivingDestinationId,
    tenderedAmount: snapshot.tenderedAmount,
    localChangeAmount: snapshot.localChangeAmount,
    verificationSource: snapshot.verificationSource,
    localCommittedAt,
  };
  const scopeKey = settlementScope(input.identity);
  const outbox: OutboxRecord = {
    requestKey: rootRequestKey,
    scopeKey,
    operation: "SETTLE_CUSTOMER_RECEIVABLE_V1",
    payload,
    state: "PENDING",
    attempts: 0,
    nextAttemptAt: null,
    lastError: null,
    createdAt: localCommittedAt,
    updatedAt: localCommittedAt,
  };

  await posRuntimeDb.transaction("rw", posRuntimeDb.journal, posRuntimeDb.outbox, async () => {
    await posRuntimeDb.journal.put({
      localId: settlementLocalId(localSettlementId),
      scopeKey,
      kind: "RECEIVABLE_SETTLEMENT",
      state: "PENDING_SERVER_CONFIRMATION",
      requestKey: rootRequestKey,
      snapshot,
      createdAt: localCommittedAt,
      updatedAt: localCommittedAt,
    });
    await posRuntimeDb.outbox.put(outbox);
  });
  return snapshot;
}

function isRetryableGatewayError(error: string) {
  return error === "GATEWAY_TIMEOUT"
    || error === "GATEWAY_UNREACHABLE"
    || error === "RECEIVABLE_SETTLEMENT_FAILED"
    || error.startsWith("GATEWAY_HTTP_5");
}

async function markRetryableFailure(record: OutboxRecord, message: string) {
  const now = new Date().toISOString();
  await posRuntimeDb.outbox.put({
    ...record,
    state: "FAILED",
    attempts: record.attempts + 1,
    nextAttemptAt: new Date(Date.now() + 5_000).toISOString(),
    lastError: message,
    updatedAt: now,
  });
}

async function markBusinessRejected(record: OutboxRecord, journal: JournalRecord, message: string) {
  const now = new Date().toISOString();
  await posRuntimeDb.transaction("rw", posRuntimeDb.journal, posRuntimeDb.outbox, async () => {
    await posRuntimeDb.journal.put({ ...journal, state: "REJECTED", updatedAt: now });
    await posRuntimeDb.outbox.put({
      ...record,
      state: "ACKED",
      attempts: record.attempts + 1,
      nextAttemptAt: null,
      lastError: message,
      updatedAt: now,
    });
  });
}

export async function syncReceivableSettlementOutbox(
  identity: TrustedTerminalIdentity,
  deviceSecret: string,
  sessionToken: string,
  requestKey: string,
) {
  const record = await posRuntimeDb.outbox.get(requestKey);
  if (!record || record.operation !== "SETTLE_CUSTOMER_RECEIVABLE_V1") throw new Error("RECEIVABLE_SETTLEMENT_OUTBOX_NOT_FOUND");
  const journal = await posRuntimeDb.journal.where("requestKey").equals(requestKey).first();
  if (!journal || journal.kind !== "RECEIVABLE_SETTLEMENT") throw new Error("RECEIVABLE_SETTLEMENT_JOURNAL_NOT_FOUND");
  const snapshot = snapshotFromJournal(journal);
  if (!snapshot) throw new Error("RECEIVABLE_SETTLEMENT_SNAPSHOT_INVALID");
  if (record.state === "ACKED" && journal.state === "CONFIRMED" && snapshot.canonical) return snapshot;

  const baseUrl = getGatewayBaseUrl();
  if (!baseUrl) throw new Error("POS_GATEWAY_NOT_CONFIGURED");
  const payload = record.payload as ReceivableSettlementOutboxPayload;
  await posRuntimeDb.outbox.put({ ...record, state: "SENDING", updatedAt: new Date().toISOString() });

  const client = new PosReceivableSettlementGatewayClient({ baseUrl });
  const result = await client.settle({
    deviceSecret,
    sessionToken,
    receivableId: payload.receivableId,
    amount: payload.amount,
    paymentMethodCode: payload.paymentMethodCode,
    receivingDestinationId: payload.receivingDestinationId,
    tenderedAmount: payload.tenderedAmount,
    localChangeAmount: payload.localChangeAmount,
    verificationSource: payload.verificationSource,
    requestKey,
    clientContext: {
      runtime: "WEBPOS",
      terminal_node_id: identity.node_id,
      local_settlement_id: snapshot.localSettlementId,
      local_committed_at: payload.localCommittedAt,
      local_commit_authority: false,
    },
  });

  if (!result.ok || !result.data) {
    const error = result.error ?? "RECEIVABLE_SETTLEMENT_FAILED";
    console.error("[receivable-settlement]", { requestKey, receivableId: payload.receivableId, error, reasonCode: result.reasonCode });
    if (isRetryableGatewayError(error)) await markRetryableFailure(record, error);
    else await markBusinessRejected(record, journal, error);
    throw new Error(error);
  }

  await applyCanonicalCustomerCreditSummaryReadback(
    identity,
    result.data.customer_credit_summary,
    result.data.receivable_balance.updated_at || new Date().toISOString(),
  );
  const ackedAt = new Date().toISOString();
  const confirmed: LocalReceivableSettlementSnapshot = { ...snapshot, canonical: result.data };
  await posRuntimeDb.transaction("rw", posRuntimeDb.journal, posRuntimeDb.outbox, async () => {
    await posRuntimeDb.journal.put({ ...journal, state: "CONFIRMED", snapshot: confirmed, updatedAt: ackedAt });
    await posRuntimeDb.outbox.put({
      ...record,
      state: "ACKED",
      attempts: record.attempts + 1,
      nextAttemptAt: null,
      lastError: null,
      updatedAt: ackedAt,
    });
  });
  await clearSettledCustomerMemberContext(identity, snapshot.customerId);
  return confirmed;
}

export async function syncPendingReceivableSettlements(
  identity: TrustedTerminalIdentity,
  deviceSecret: string,
  sessionToken: string,
) {
  const pending = await posRuntimeDb.outbox
    .where("operation")
    .equals("SETTLE_CUSTOMER_RECEIVABLE_V1")
    .filter((record) => record.state !== "ACKED")
    .sortBy("createdAt");
  const results: Array<{ requestKey: string; ok: boolean; error?: string }> = [];
  for (const record of pending) {
    if (record.scopeKey !== settlementScope(identity)) continue;
    if (record.nextAttemptAt && Date.parse(record.nextAttemptAt) > Date.now()) continue;
    try {
      await syncReceivableSettlementOutbox(identity, deviceSecret, sessionToken, record.requestKey);
      results.push({ requestKey: record.requestKey, ok: true });
    } catch (error) {
      results.push({
        requestKey: record.requestKey,
        ok: false,
        error: error instanceof Error ? error.message : "RECEIVABLE_SETTLEMENT_FAILED",
      });
    }
  }
  return results;
}
